// Phase 2 — per-portal configuration with tabs:
//   • Overview  — name, description, request types (Phase 1 content)
//   • Groups    — portal groups + drag-to-assign request types
//   • Branding  — logo, hero, intro text, theme colour
//   • Domain    — subdomain + custom domain + DNS verification
// Admins-only. Service Desk add-on must be enabled.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ArrowLeft, Plus, Trash, CheckCircle, Warning, Copy, ArrowSquareOut, CaretUp, CaretDown, Lightning } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { CategoryBadge, CATEGORY_OPTIONS, categoryLabel } from '../../components/serviceDesk/ticketMeta';
import { ALL_QUEUE_COLUMNS, DEFAULT_QUEUE_COLUMNS, QUEUE_COLUMN_LABELS } from '../../components/serviceDesk/queueColumns';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';

// Small segmented control letting admins choose how an uploaded image fills its
// frame on the public portal: 'contain' (show the whole image — best for logos)
// or 'cover' (crop to fill — best for wide photos).
function ImageFitToggle({ value = 'contain', onChange, testid }) {
  const opts = [
    { key: 'contain', label: 'Fit' },
    { key: 'cover', label: 'Fill' },
  ];
  return (
    <div className="inline-flex items-center rounded-md bg-zinc-100 p-0.5 ring-1 ring-zinc-200" data-testid={testid}>
      {opts.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
          className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${value === o.key ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-800'}`}
          data-testid={`${testid}-${o.key}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function PortalConfigPage() {
  const { portalId } = useParams();
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [requestTypes, setRequestTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);

  const sdEnabled = caps?.service_desk_enabled === true;
  const isAdmin = ['owner', 'admin'].includes(currentOrg?.role);
  const canManage = ['owner', 'admin', 'project_manager'].includes(currentOrg?.role);

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) { setLoading(false); return; }
    setLoading(true);
    try {
      const [portalsRes, rtRes, gRes, dRes] = await Promise.all([
        axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/portals/${portalId}/request-types`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/portals/${portalId}/groups`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/portals/${portalId}/domain`, { withCredentials: true, headers: getOrgHeaders() }),
      ]);
      setPortal((portalsRes.data || []).find(p => p.portal_id === portalId) || null);
      setRequestTypes(rtRes.data || []);
      setGroups(gRes.data || []);
      setDomain(dRes.data || null);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, portalId, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (!canManage) return <div className="flex-1 flex items-center justify-center bg-zinc-50"><p className="text-sm text-zinc-500">Managers only.</p></div>;
  if (loading) return <div className="flex-1 flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!portal) return <div className="flex-1 flex items-center justify-center text-zinc-400">Portal not found</div>;

  const savePortal = async (patch) => {
    try {
      const { data } = await axios.patch(`${API}/service-desk/portals/${portalId}`, patch,
        { withCredentials: true, headers: getOrgHeaders() });
      setPortal(prev => ({ ...prev, ...data }));
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save');
    }
  };

  const portalUrl = portal.domain_verified && portal.custom_domain && portal.subdomain
    ? `https://${portal.subdomain}.${portal.custom_domain}`
    : `${window.location.origin}/help/${portal.subdomain || portal.key.toLowerCase()}`;

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="sd-portal-config-page">
      <div className="max-w-5xl mx-auto px-8 py-6">
        <button onClick={() => navigate('/service-desk/settings')} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Back to portals
        </button>

        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md flex items-center justify-center text-white font-bold text-sm ring-1 ring-white/10"
              style={{ background: portal.branding?.theme_color || '#2563eb' }}>
              {portal.key}
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">{portal.name}</h1>
              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <span>{portal.ticket_counter || 0} tickets · key {portal.key}</span>
                {portal.is_published ? (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200">PUBLISHED</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 text-[10px] font-semibold">DRAFT</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch checked={!!portal.is_published} onCheckedChange={(v) => savePortal({ is_published: v })}
                data-testid="sd-portal-publish-toggle" />
              <span className="text-xs font-medium text-zinc-700">{portal.is_published ? 'Live' : 'Off'}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open(portalUrl, '_blank')}
              disabled={!portal.is_published} data-testid="sd-portal-open-public">
              <ArrowSquareOut size={13} className="mr-1.5" /> View portal
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="overview" data-testid="sd-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="groups" data-testid="sd-tab-groups">Portal groups</TabsTrigger>
            <TabsTrigger value="agents" data-testid="sd-tab-agents">Agents</TabsTrigger>
            <TabsTrigger value="customers" data-testid="sd-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="routing" data-testid="sd-tab-routing">SLA &amp; Routing</TabsTrigger>
            <TabsTrigger value="listview" data-testid="sd-tab-listview">List view</TabsTrigger>
            {isAdmin && <TabsTrigger value="branding" data-testid="sd-tab-branding">Branding</TabsTrigger>}
            {isAdmin && <TabsTrigger value="domain" data-testid="sd-tab-domain">Domain</TabsTrigger>}
          </TabsList>

          {/* OVERVIEW ─────────────────────────────────────────── */}
          <TabsContent value="overview">
            <OverviewTab portal={portal} setPortal={setPortal} requestTypes={requestTypes} setRequestTypes={setRequestTypes}
              groups={groups} savePortal={savePortal} API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
          </TabsContent>

          {/* GROUPS ─────────────────────────────────────────── */}
          <TabsContent value="groups">
            <GroupsTab groups={groups} setGroups={setGroups} requestTypes={requestTypes} setRequestTypes={setRequestTypes}
              portal={portal} savePortal={savePortal} API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
          </TabsContent>

          {/* AGENTS ─────────────────────────────────────────── */}
          <TabsContent value="agents">
            <AgentsTab API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
          </TabsContent>

          {/* CUSTOMERS ─────────────────────────────────────────── */}
          <TabsContent value="customers">
            <CustomersTab portal={portal} savePortal={savePortal} API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
          </TabsContent>

          {/* SLA & ROUTING ─────────────────────────────────────── */}
          <TabsContent value="routing">
            <SlaRoutingTab portal={portal} savePortal={savePortal} requestTypes={requestTypes}
              API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
          </TabsContent>

          {/* LIST VIEW ─────────────────────────────────────────── */}
          <TabsContent value="listview">
            <ListViewTab portal={portal} savePortal={savePortal} />
          </TabsContent>

          {/* BRANDING ─────────────────────────────────────────── */}
          {isAdmin && (
            <TabsContent value="branding">
              <BrandingTab portal={portal} savePortal={savePortal} />
            </TabsContent>
          )}

          {/* DOMAIN ─────────────────────────────────────────── */}
          {isAdmin && (
            <TabsContent value="domain">
              <DomainTab portal={portal} domain={domain} savePortal={savePortal} reload={load}
                API={API} getOrgHeaders={getOrgHeaders} portalId={portalId} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// ─── Sub-tab: Overview ──────────────────────────────────
function OverviewTab({ portal, setPortal, requestTypes, setRequestTypes, groups, savePortal, API, getOrgHeaders, portalId }) {
  const navigate = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const [n, setN] = useState(''); const [d, setD] = useState(''); const [groupId, setGroupId] = useState('__none');
  const [cat, setCat] = useState('service_request');
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!n.trim()) return toast.error('Name is required');
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/portals/${portalId}/request-types`,
        { name: n.trim(), description: d.trim(), category: cat, portal_group_id: groupId === '__none' ? null : groupId },
        { withCredentials: true, headers: getOrgHeaders() });
      setRequestTypes(prev => [...prev, data]);
      setShowNew(false); setN(''); setD(''); setGroupId('__none'); setCat('service_request');
      toast.success('Request type created');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setCreating(false); }
  };

  const setCategory = async (rtId, category) => {
    try {
      const { data } = await axios.patch(`${API}/service-desk/request-types/${rtId}`, { category },
        { withCredentials: true, headers: getOrgHeaders() });
      setRequestTypes(prev => prev.map(rt => rt.request_type_id === rtId ? { ...rt, category: data.category || category } : rt));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const del = async (rtId) => {
    if (!window.confirm('Delete this request type?')) return;
    try {
      await axios.delete(`${API}/service-desk/request-types/${rtId}`, { withCredentials: true, headers: getOrgHeaders() });
      setRequestTypes(prev => prev.filter(rt => rt.request_type_id !== rtId));
      toast.success('Deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const rtFileRefs = useRef({});
  const [uploadingRt, setUploadingRt] = useState(null);

  const patchRt = async (rtId, patch) => {
    try {
      await axios.patch(`${API}/service-desk/request-types/${rtId}`, patch, { withCredentials: true, headers: getOrgHeaders() });
      setRequestTypes(prev => prev.map(rt => rt.request_type_id === rtId ? { ...rt, ...patch } : rt));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const uploadRtImage = async (rtId, file) => {
    if (!file) return;
    setUploadingRt(rtId);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/service-desk/request-types/${rtId}/image`, fd, { withCredentials: true, headers: getOrgHeaders() });
      let url = data.image_url;
      if (url) url = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`; // cache-bust on replace
      setRequestTypes(prev => prev.map(rt => rt.request_type_id === rtId ? { ...rt, image_url: url } : rt));
      toast.success('Icon uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingRt(null); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Portal name</Label>
            <Input value={portal.name} onChange={(e) => setPortal(prev => ({ ...prev, name: e.target.value }))}
              onBlur={(e) => savePortal({ name: e.target.value })} data-testid="sd-config-portal-name" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={portal.description || ''} onChange={(e) => setPortal(prev => ({ ...prev, description: e.target.value }))}
              onBlur={(e) => savePortal({ description: e.target.value })} data-testid="sd-config-portal-desc" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Request types</h2>
            <p className="text-xs text-zinc-500">What requesters can pick when raising a ticket.</p>
          </div>
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="sd-new-rt-btn">
            <Plus size={14} className="mr-1.5" /> New request type
          </Button>
        </div>
        {requestTypes.length === 0 ? (
          <p className="text-xs text-zinc-400 py-4 text-center">No request types yet.</p>
        ) : (
          <div className="space-y-2">
            {requestTypes.map(rt => {
              const g = groups.find(x => x.group_id === rt.portal_group_id);
              return (
                <div key={rt.request_type_id} className="p-3 border border-zinc-200 rounded-md"
                  data-testid={`sd-rt-row-${rt.request_type_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      {rt.image_url ? (
                        <img src={rt.image_url} alt="" className={`w-9 h-9 rounded-md ring-1 ring-zinc-200 bg-zinc-50 shrink-0 ${(rt.image_fit || 'contain') === 'contain' ? 'object-contain p-0.5' : 'object-cover'}`} data-testid={`sd-rt-image-${rt.request_type_id}`} />
                      ) : (
                        <div className="w-9 h-9 rounded-md flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ background: portal.branding?.theme_color || '#2563eb' }}>{(rt.name || '?').charAt(0).toUpperCase()}</div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-zinc-900">{rt.name}</p>
                          <CategoryBadge category={rt.category} />
                        </div>
                        {rt.description && <p className="text-xs text-zinc-500 truncate">{rt.description}</p>}
                        <p className="text-[10px] text-zinc-400 mt-0.5">
                          Group: {g?.name || 'Unassigned'} · {(rt.form_fields || []).length} custom field{(rt.form_fields || []).length === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Select value={rt.category || 'service_request'} onValueChange={(v) => setCategory(rt.request_type_id, v)}>
                        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`sd-rt-category-${rt.request_type_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/service-desk/settings/portal/${portalId}/form/${rt.request_type_id}`)}
                        data-testid={`sd-rt-configure-form-${rt.request_type_id}`}>
                        Configure form
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => del(rt.request_type_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`sd-rt-delete-${rt.request_type_id}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  </div>
                  {/* Icon image upload + fit — shown on the small catalog card. */}
                  <div className="flex items-center gap-2 mt-2 pl-12">
                    <input ref={(el) => { rtFileRefs.current[rt.request_type_id] = el; }} type="file" accept="image/*" className="hidden"
                      onChange={(e) => uploadRtImage(rt.request_type_id, e.target.files?.[0])} data-testid={`sd-rt-image-file-${rt.request_type_id}`} />
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => rtFileRefs.current[rt.request_type_id]?.click()} disabled={uploadingRt === rt.request_type_id}
                      data-testid={`sd-rt-image-upload-${rt.request_type_id}`}>
                      {uploadingRt === rt.request_type_id ? 'Uploading…' : (rt.image_url ? 'Replace icon' : 'Upload icon')}
                    </Button>
                    {rt.image_url && (
                      <>
                        <span className="text-[11px] text-zinc-500">Display:</span>
                        <ImageFitToggle value={rt.image_fit || 'contain'} onChange={(v) => patchRt(rt.request_type_id, { image_fit: v })} testid={`sd-rt-fit-${rt.request_type_id}`} />
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => patchRt(rt.request_type_id, { image_url: null })}
                          data-testid={`sd-rt-image-remove-${rt.request_type_id}`}>Remove</Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-md" data-testid="sd-new-rt-dialog">
          <DialogHeader><DialogTitle>New request type</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs">Name</Label><Input value={n} onChange={(e)=>setN(e.target.value)} placeholder="e.g., Password reset" data-testid="sd-new-rt-name" /></div>
            <div><Label className="text-xs">Description (optional)</Label><Textarea rows={2} value={d} onChange={(e)=>setD(e.target.value)} data-testid="sd-new-rt-desc" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger data-testid="sd-new-rt-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map(c => <SelectItem key={c} value={c}>{categoryLabel(c)}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-400 mt-1">Service Request (something needed) · Incident (something broken) · Problem · Change.</p>
            </div>
            <div>
              <Label className="text-xs">Group (optional)</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Unassigned</SelectItem>
                  {groups.map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowNew(false)}>Cancel</Button>
            <Button onClick={create} disabled={creating} data-testid="sd-new-rt-submit">{creating?'Creating…':'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sub-tab: Groups ──────────────────────────────────
// ─── Sub-tab: List view (configurable queue columns) ──────────────────────────────────
function ListViewTab({ portal, savePortal }) {
  const [cols, setCols] = useState(portal.queue_columns?.length ? portal.queue_columns : DEFAULT_QUEUE_COLUMNS);
  const [saving, setSaving] = useState(false);

  const persist = async (next) => {
    setCols(next);
    setSaving(true);
    try { await savePortal({ queue_columns: next }); } finally { setSaving(false); }
  };
  const toggle = (key) => {
    if (key === 'key' || key === 'title') return; // always shown
    if (cols.includes(key)) persist(cols.filter(c => c !== key));
    else persist([...cols, key]);
  };
  const move = (key, dir) => {
    const i = cols.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cols.length) return;
    const next = [...cols];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };
  const disabled = ALL_QUEUE_COLUMNS.map(c => c.key).filter(k => !cols.includes(k));

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="sd-listview-tab">
      <h2 className="text-sm font-semibold text-zinc-900">List view columns</h2>
      <p className="text-xs text-zinc-500 mb-4">Choose which columns appear in this portal's ticket queue and their order. Key and Title are always shown.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Shown columns <span className="text-zinc-300 normal-case font-normal">· drag order with arrows</span></p>
          <div className="space-y-1.5">
            {cols.map((key, idx) => {
              const locked = key === 'key' || key === 'title';
              return (
                <div key={key} className="flex items-center gap-2 p-2 rounded-md border border-zinc-200 bg-zinc-50" data-testid={`sd-col-row-${key}`}>
                  <input type="checkbox" checked readOnly={locked} disabled={locked} onChange={() => toggle(key)} data-testid={`sd-col-toggle-${key}`} />
                  <span className="text-sm text-zinc-800 flex-1">{QUEUE_COLUMN_LABELS[key] || key}</span>
                  <button onClick={() => move(key, -1)} disabled={idx === 0} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`sd-col-up-${key}`}><CaretUp size={14} /></button>
                  <button onClick={() => move(key, 1)} disabled={idx === cols.length - 1} className="p-1 text-zinc-400 hover:text-zinc-700 disabled:opacity-30" data-testid={`sd-col-down-${key}`}><CaretDown size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Available columns</p>
          {disabled.length === 0 ? (
            <div className="border border-dashed border-zinc-200 rounded-md p-6 text-center">
              <p className="text-xs text-zinc-400">All columns are shown.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {disabled.map(key => (
                <div key={key} className="flex items-center gap-2 p-2 rounded-md border border-dashed border-zinc-200" data-testid={`sd-col-row-${key}`}>
                  <input type="checkbox" checked={false} onChange={() => toggle(key)} data-testid={`sd-col-toggle-${key}`} />
                  <span className="text-sm text-zinc-600 flex-1">{QUEUE_COLUMN_LABELS[key] || key}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {saving && <p className="text-[11px] text-zinc-400 mt-3">Saving…</p>}
    </div>
  );
}


// Tiny visual preview of each catalog layout option.
function LayoutPreview({ kind }) {
  if (kind === 'list') {
    return (
      <div className="space-y-1 h-14 bg-zinc-50 rounded p-1.5">
        {[0, 1, 2].map(i => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-300 shrink-0" />
            <div className="h-1.5 flex-1 rounded bg-zinc-200" />
          </div>
        ))}
      </div>
    );
  }
  if (kind === 'compact') {
    return (
      <div className="grid grid-cols-4 gap-1 h-14 bg-zinc-50 rounded p-1.5">
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <div key={i} className="rounded bg-blue-200/70" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-1 h-14 bg-zinc-50 rounded p-1.5">
      {[0, 1, 2].map(i => (
        <div key={i} className="rounded bg-white border border-zinc-200 overflow-hidden flex flex-col">
          <div className="h-1/2 bg-blue-300" />
          <div className="flex-1" />
        </div>
      ))}
    </div>
  );
}

function GroupsTab({ groups, setGroups, requestTypes, setRequestTypes, portal, savePortal, API, getOrgHeaders, portalId }) {
  const [newName, setNewName] = useState('');
  const [uploadingGroup, setUploadingGroup] = useState(null);
  const [layout, setLayout] = useState(portal?.catalog_layout || 'image_cards');
  const fileRefs = useRef({});

  const LAYOUTS = [
    { key: 'image_cards', label: 'Image cards', hint: 'Large cover-image cards (3 per row)' },
    { key: 'compact', label: 'Compact', hint: 'Small icon tiles, denser grid' },
    { key: 'list', label: 'List', hint: 'Horizontal rows with description' },
  ];
  const chooseLayout = async (key) => { setLayout(key); try { await savePortal({ catalog_layout: key }); } catch (_) {} };

  const add = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await axios.post(`${API}/service-desk/portals/${portalId}/groups`,
        { name: newName.trim() }, { withCredentials: true, headers: getOrgHeaders() });
      setGroups(prev => [...prev, data]);
      setNewName('');
      toast.success('Group created');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const patchGroup = async (groupId, patch) => {
    try {
      await axios.patch(`${API}/service-desk/portal-groups/${groupId}`, patch,
        { withCredentials: true, headers: getOrgHeaders() });
      setGroups(prev => prev.map(g => g.group_id === groupId ? { ...g, ...patch } : g));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const rename = (groupId, name) => patchGroup(groupId, { name });

  const uploadImage = async (groupId, file) => {
    if (!file) return;
    setUploadingGroup(groupId);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/service-desk/portal-groups/${groupId}/image`, fd,
        { withCredentials: true, headers: getOrgHeaders() });
      let url = data.image_url;
      if (url) url = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`; // cache-bust on replace
      setGroups(prev => prev.map(g => g.group_id === groupId ? { ...g, image_url: url } : g));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploadingGroup(null); }
  };

  const removeImage = async (groupId) => {
    try {
      await axios.patch(`${API}/service-desk/portal-groups/${groupId}`, { image_url: null },
        { withCredentials: true, headers: getOrgHeaders() });
      setGroups(prev => prev.map(g => g.group_id === groupId ? { ...g, image_url: null } : g));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const del = async (groupId) => {
    if (!window.confirm('Delete this group? Request types inside become unassigned.')) return;
    try {
      await axios.delete(`${API}/service-desk/portal-groups/${groupId}`, { withCredentials: true, headers: getOrgHeaders() });
      setGroups(prev => prev.filter(g => g.group_id !== groupId));
      setRequestTypes(prev => prev.map(rt => rt.portal_group_id === groupId ? { ...rt, portal_group_id: null } : rt));
      toast.success('Group deleted');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const moveRt = async (rtId, newGroupId) => {
    try {
      const body = { portal_group_id: newGroupId === '__none' ? null : newGroupId };
      const { data } = await axios.patch(`${API}/service-desk/request-types/${rtId}`, body,
        { withCredentials: true, headers: getOrgHeaders() });
      setRequestTypes(prev => prev.map(rt => rt.request_type_id === rtId ? data : rt));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const unassigned = requestTypes.filter(rt => !rt.portal_group_id);

  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="sd-catalog-layout">
        <h2 className="text-sm font-semibold text-zinc-900">Customer portal layout</h2>
        <p className="text-xs text-zinc-500 mb-4">Choose how request-type groups appear to customers on your help center home page.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {LAYOUTS.map(l => {
            const active = layout === l.key;
            return (
              <button key={l.key} onClick={() => chooseLayout(l.key)} data-testid={`sd-layout-${l.key}`}
                className={`text-left rounded-lg border p-3 transition-all ${active ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/40' : 'border-zinc-200 hover:border-zinc-300'}`}>
                <LayoutPreview kind={l.key} />
                <p className="text-sm font-medium text-zinc-900 mt-2">{l.label}</p>
                <p className="text-[11px] text-zinc-500">{l.hint}</p>
              </button>
            );
          })}
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Input value={newName} onChange={(e)=>setNewName(e.target.value)} placeholder="New group name (e.g., Technical Issues)"
            data-testid="sd-new-group-input" />
          <Button onClick={add} data-testid="sd-new-group-btn"><Plus size={14} className="mr-1" /> Add</Button>
        </div>
        <p className="text-xs text-zinc-500 mb-3">Group request types together so customers can browse the portal homepage by category.</p>

        <div className="space-y-3">
          {groups.map(g => {
            const inGroup = requestTypes.filter(rt => rt.portal_group_id === g.group_id);
            return (
              <div key={g.group_id} className="border border-zinc-200 rounded-md p-3" data-testid={`sd-group-card-${g.group_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Input value={g.name} onChange={(e) => setGroups(prev => prev.map(x => x.group_id === g.group_id ? { ...x, name: e.target.value } : x))}
                    onBlur={(e) => rename(g.group_id, e.target.value)} className="font-semibold flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => del(g.group_id)} className="text-red-600">
                    <Trash size={13} />
                  </Button>
                </div>

                {/* Cover image — becomes the big card on the portal homepage. */}
                <div className="flex items-center gap-3 mb-2">
                  {g.image_url ? (
                    <img src={g.image_url} alt="cover" className={`w-24 h-14 rounded ring-1 ring-zinc-200 bg-zinc-50 ${(g.image_fit || 'contain') === 'contain' ? 'object-contain p-1' : 'object-cover'}`} data-testid={`sd-group-image-${g.group_id}`} />
                  ) : (
                    <div className="w-24 h-14 rounded bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center text-[10px] text-zinc-400">No image</div>
                  )}
                  <input ref={(el) => { fileRefs.current[g.group_id] = el; }} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadImage(g.group_id, e.target.files?.[0])} data-testid={`sd-group-image-file-${g.group_id}`} />
                  <Button variant="outline" size="sm" onClick={() => fileRefs.current[g.group_id]?.click()} disabled={uploadingGroup === g.group_id}
                    data-testid={`sd-group-image-upload-${g.group_id}`}>
                    {uploadingGroup === g.group_id ? 'Uploading…' : (g.image_url ? 'Replace cover' : 'Upload cover')}
                  </Button>
                  {g.image_url && (
                    <Button variant="ghost" size="sm" onClick={() => removeImage(g.group_id)} className="text-red-600"
                      data-testid={`sd-group-image-remove-${g.group_id}`}>Remove</Button>
                  )}
                </div>
                {g.image_url && (
                  <div className="flex items-center gap-2 mb-2" data-testid={`sd-group-fit-${g.group_id}`}>
                    <span className="text-[11px] text-zinc-500">Display:</span>
                    <ImageFitToggle value={g.image_fit || 'contain'} onChange={(v) => patchGroup(g.group_id, { image_fit: v })} testid={`sd-group-fit-toggle-${g.group_id}`} />
                  </div>
                )}
                <Input value={g.description || ''} placeholder="Short description (shown on the portal card)"
                  onChange={(e) => setGroups(prev => prev.map(x => x.group_id === g.group_id ? { ...x, description: e.target.value } : x))}
                  onBlur={(e) => patchGroup(g.group_id, { description: e.target.value })} className="mb-2 text-sm"
                  data-testid={`sd-group-desc-${g.group_id}`} />

                {inGroup.length === 0 ? <p className="text-xs text-zinc-400 italic">No request types in this group.</p> : (
                  <div className="space-y-1">
                    {inGroup.map(rt => (
                      <div key={rt.request_type_id} className="flex items-center justify-between text-sm py-1 px-2 bg-zinc-50 rounded">
                        <span>{rt.name}</span>
                        <Select value={rt.portal_group_id || '__none'} onValueChange={(v) => moveRt(rt.request_type_id, v)}>
                          <SelectTrigger className="w-40 h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">Unassigned</SelectItem>
                            {groups.map(g2 => <SelectItem key={g2.group_id} value={g2.group_id}>{g2.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {unassigned.length > 0 && (
            <div className="border border-dashed border-zinc-300 rounded-md p-3">
              <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Unassigned</p>
              <div className="space-y-1">
                {unassigned.map(rt => (
                  <div key={rt.request_type_id} className="flex items-center justify-between text-sm py-1 px-2 bg-zinc-50 rounded">
                    <span>{rt.name}</span>
                    <Select value="__none" onValueChange={(v) => moveRt(rt.request_type_id, v)}>
                      <SelectTrigger className="w-40 h-7 text-xs"><SelectValue placeholder="Move to…" /></SelectTrigger>
                      <SelectContent>
                        {groups.map(g2 => <SelectItem key={g2.group_id} value={g2.group_id}>{g2.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-tab: Branding ──────────────────────────────────
function BrandingTab({ portal, savePortal }) {
  const { API, getOrgHeaders } = useAuth();
  const b = portal.branding || {};
  const [draft, setDraft] = useState({
    intro_text: b.intro_text || '',
    theme_color: b.theme_color || '#2563eb',
    logo_url: b.logo_url || '',
    hero_image_url: b.hero_image_url || '',
    hero_fit: b.hero_fit || 'cover',
    footer_text: b.footer_text || '',
    powered_by_label: b.powered_by_label || '',
    powered_by_url: b.powered_by_url || '',
  });
  const [uploading, setUploading] = useState(null); // 'logo' | 'hero' | null
  const logoRef = useRef(null);
  const heroRef = useRef(null);
  const save = (patch) => savePortal({ branding: patch });

  const uploadImage = async (kind, file) => {
    if (!file) return;
    setUploading(kind);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const url = `${API}/service-desk/portals/${portal.portal_id}/${kind}`;
      const { data } = await axios.post(url, fd, { withCredentials: true, headers: getOrgHeaders() });
      let newUrl = kind === 'logo' ? data.logo_url : data.hero_image_url;
      // Cache-bust: same filename can be reused on Replace, so append a
      // timestamp to force the <img> to refetch instead of showing a stale
      // tile until the user reloads.
      if (newUrl) newUrl = `${newUrl}${newUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
      setDraft(d => ({ ...d, [kind === 'logo' ? 'logo_url' : 'hero_image_url']: newUrl }));
      toast.success(`${kind === 'logo' ? 'Logo' : 'Hero image'} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(null); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
        <h3 className="text-sm font-semibold text-zinc-900">Branding</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Theme colour</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={draft.theme_color} onChange={(e)=>setDraft(d=>({...d,theme_color:e.target.value}))}
                onBlur={()=>save({theme_color:draft.theme_color})} className="w-12 h-9 rounded border border-zinc-200 cursor-pointer"
                data-testid="sd-brand-color" />
              <Input value={draft.theme_color} onChange={(e)=>setDraft(d=>({...d,theme_color:e.target.value}))}
                onBlur={()=>save({theme_color:draft.theme_color})} className="flex-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {draft.logo_url ? (
                <img src={draft.logo_url} alt="logo" className="w-12 h-12 rounded ring-1 ring-zinc-200 object-contain bg-zinc-50 p-1" />
              ) : (
                <div className="w-12 h-12 rounded bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center text-[10px] text-zinc-400">No logo</div>
              )}
              <input ref={logoRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadImage('logo', e.target.files?.[0])} data-testid="sd-brand-logo-file" />
              <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading === 'logo'}
                data-testid="sd-brand-logo-upload">
                {uploading === 'logo' ? 'Uploading…' : (draft.logo_url ? 'Replace' : 'Upload')}
              </Button>
              {draft.logo_url && (
                <Button variant="ghost" size="sm" onClick={() => { setDraft(d => ({ ...d, logo_url: '' })); save({ logo_url: null }); }}
                  className="text-red-600">Remove</Button>
              )}
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">PNG / JPG / SVG up to 5 MB.</p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Hero background image (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              {draft.hero_image_url ? (
                <img src={draft.hero_image_url} alt="hero" className="w-32 h-16 rounded ring-1 ring-zinc-200 object-cover" />
              ) : (
                <div className="w-32 h-16 rounded bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center text-[10px] text-zinc-400">No hero</div>
              )}
              <input ref={heroRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => uploadImage('hero', e.target.files?.[0])} data-testid="sd-brand-hero-file" />
              <Button variant="outline" size="sm" onClick={() => heroRef.current?.click()} disabled={uploading === 'hero'}
                data-testid="sd-brand-hero-upload">
                {uploading === 'hero' ? 'Uploading…' : (draft.hero_image_url ? 'Replace' : 'Upload')}
              </Button>
              {draft.hero_image_url && (
                <Button variant="ghost" size="sm" onClick={() => { setDraft(d => ({ ...d, hero_image_url: '' })); save({ hero_image_url: null }); }}
                  className="text-red-600">Remove</Button>
              )}
            </div>
            {draft.hero_image_url && (
              <div className="flex items-center gap-2 mt-2" data-testid="sd-brand-hero-fit">
                <span className="text-[11px] text-zinc-500">Display:</span>
                <ImageFitToggle value={draft.hero_fit} onChange={(v) => { setDraft(d => ({ ...d, hero_fit: v })); save({ hero_fit: v }); }} testid="sd-brand-hero-fit-toggle" />
              </div>
            )}
            <p className="text-[10px] text-zinc-400 mt-1">Wide landscape image (1600×400 ideal). Rendered with reduced opacity behind the hero text.</p>
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Intro text</Label>
            <Textarea rows={3} value={draft.intro_text} onChange={(e)=>setDraft(d=>({...d,intro_text:e.target.value}))}
              onBlur={()=>save({intro_text:draft.intro_text})} placeholder="Welcome! Raise a request and we'll be in touch." data-testid="sd-brand-intro" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Footer text (optional)</Label>
            <Input value={draft.footer_text} onChange={(e)=>setDraft(d=>({...d,footer_text:e.target.value}))}
              onBlur={()=>save({footer_text:draft.footer_text||null})} placeholder="© 2026 Your Company · support hours…" />
          </div>
          <div className="col-span-2 border-t border-zinc-100 pt-4">
            <Label className="text-xs font-semibold text-zinc-700">"Powered by" branding</Label>
            <p className="text-[10px] text-zinc-400 mt-0.5 mb-2">White-label the centered footer line. Leave the label blank to show "Inspiro Service Desk".</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-zinc-500">Powered-by label</Label>
                <Input value={draft.powered_by_label} onChange={(e)=>setDraft(d=>({...d,powered_by_label:e.target.value}))}
                  onBlur={()=>save({powered_by_label:draft.powered_by_label||null})} placeholder="Inspiro Service Desk"
                  data-testid="sd-brand-poweredby-label" />
              </div>
              <div>
                <Label className="text-[11px] text-zinc-500">Link URL (optional)</Label>
                <Input value={draft.powered_by_url} onChange={(e)=>setDraft(d=>({...d,powered_by_url:e.target.value}))}
                  onBlur={()=>save({powered_by_url:draft.powered_by_url||null})} placeholder="https://yourcompany.com"
                  data-testid="sd-brand-poweredby-url" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold mb-3">Preview</p>
        <div className="rounded-lg overflow-hidden ring-1 ring-zinc-200">
          <div className="relative px-6 py-8 text-white" style={{ background: draft.theme_color }}>
            {draft.hero_image_url && (
              <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url('${draft.hero_image_url}')` }} aria-hidden />
            )}
            <div className="relative">
              <div className="flex items-center gap-3 mb-3">
                {draft.logo_url ? <img src={draft.logo_url} alt="logo" className="w-10 h-10 rounded bg-white object-contain p-1" /> : <div className="w-10 h-10 rounded bg-white/20" />}
                <h2 className="text-2xl font-bold font-['Outfit']">{portal.name}</h2>
              </div>
              <p className="text-white/90 text-sm max-w-2xl">{draft.intro_text || 'Welcome…'}</p>
            </div>
          </div>
          <div className="bg-zinc-50 px-6 py-4 flex flex-col items-center gap-1.5 border-t border-zinc-200">
            {draft.footer_text && <span className="text-xs text-zinc-500">{draft.footer_text}</span>}
            <span className="inline-flex items-center gap-1.5 text-xs text-zinc-400">
              Powered by
              <Lightning weight="fill" className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-semibold text-zinc-500">{draft.powered_by_label || 'Inspiro Service Desk'}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-tab: Domain ──────────────────────────────────
function DomainTab({ portal, domain, savePortal, reload, API, getOrgHeaders, portalId }) {
  const [subdomain, setSubdomain] = useState(portal.subdomain || '');
  const [customDomain, setCustomDomain] = useState(portal.custom_domain || '');
  const [verifying, setVerifying] = useState(false);
  const cnameHost = subdomain && customDomain ? `${subdomain}.${customDomain}` : null;
  const cnameTarget = domain?.cname_target || 'helpcentre.inspiroworks.com';

  const saveDomain = async () => {
    const updated = await savePortal({ subdomain, custom_domain: customDomain || null });
    if (updated) reload();
  };

  const verify = async (force = false) => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/portals/${portalId}/domain/verify`, { force },
        { withCredentials: true, headers: getOrgHeaders() });
      if (data.verified) toast.success(`Verified ${data.host}`);
      else toast.error(data.reason || 'Not verified yet');
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setVerifying(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-1">Portal URL</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Pick a subdomain, then point any company domain at our service-desk host via a single CNAME DNS record.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-xs">Subdomain</Label>
            <Input value={subdomain} onChange={(e)=>setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="support" data-testid="sd-domain-subdomain" />
            <p className="text-[10px] text-zinc-400 mt-1">Lowercase letters, digits, hyphens. e.g. support, help, it</p>
          </div>
          <div>
            <Label className="text-xs">Custom domain</Label>
            <Input value={customDomain} onChange={(e)=>setCustomDomain(e.target.value.toLowerCase().trim())}
              placeholder="acme.com" data-testid="sd-domain-custom" />
            <p className="text-[10px] text-zinc-400 mt-1">The root domain you own (no https://, no path).</p>
          </div>
        </div>
        <Button size="sm" onClick={saveDomain} data-testid="sd-domain-save">Save URL</Button>
      </div>

      {cnameHost && (
        <div className="bg-white border border-zinc-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-zinc-900">DNS verification</h3>
            {portal.domain_verified ? (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold ring-1 ring-emerald-200">
                <CheckCircle size={12} weight="fill" /> Verified
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] font-semibold ring-1 ring-amber-200">
                <Warning size={12} weight="fill" /> Not verified
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mb-3">Add this CNAME record at your DNS provider (Cloudflare, Route 53, GoDaddy, etc.):</p>
          <div className="bg-zinc-50 border border-zinc-200 rounded-md overflow-hidden text-sm">
            <table className="w-full">
              <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-white border-b border-zinc-200">
                <tr><th className="text-left py-2 px-3 font-semibold">Type</th><th className="text-left py-2 px-3 font-semibold">Name / Host</th><th className="text-left py-2 px-3 font-semibold">Value</th></tr>
              </thead>
              <tbody className="font-mono text-xs">
                <tr>
                  <td className="py-2 px-3 font-bold align-top">CNAME</td>
                  <td className="py-2 px-3 align-top">
                    <div className="flex items-center gap-2">{cnameHost}
                      <button onClick={() => { navigator.clipboard.writeText(cnameHost); toast.success('Copied'); }} className="text-zinc-400 hover:text-zinc-700">
                        <Copy size={12} />
                      </button>
                    </div>
                  </td>
                  <td className="py-2 px-3 align-top">
                    <div className="flex items-center gap-2">{cnameTarget}
                      <button onClick={() => { navigator.clipboard.writeText(cnameTarget); toast.success('Copied'); }} className="text-zinc-400 hover:text-zinc-700">
                        <Copy size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" onClick={() => verify(false)} disabled={verifying} data-testid="sd-domain-verify">
              {verifying ? 'Checking…' : 'Verify DNS'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => verify(true)} disabled={verifying}
              className="text-zinc-500" data-testid="sd-domain-force">
              Force verify (dev)
            </Button>
          </div>
          {portal.domain_verified && (
            <p className="text-[11px] text-emerald-700 mt-2 flex items-center gap-1">
              <CheckCircle size={11} weight="fill" /> Customers can reach your portal at <a href={`https://${cnameHost}`} target="_blank" rel="noreferrer" className="font-semibold underline">https://{cnameHost}</a>
            </p>
          )}
          <p className="text-[11px] text-zinc-500 mt-2">
            Path-based fallback (always works): <a href={`/help/${subdomain}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">{window.location.origin}/help/{subdomain}</a>
          </p>
        </div>
      )}
    </div>
  );
}


// ─── Sub-tab: Agents (per-portal staffing) ──────────────────────────────
function AgentsTab({ API, getOrgHeaders, portalId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/service-desk/portals/${portalId}/agents`,
        { withCredentials: true, headers: getOrgHeaders() });
      setMembers(data.members || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load agents'); }
    finally { setLoading(false); }
  }, [API, getOrgHeaders, portalId]);

  useEffect(() => { load(); }, [load]);

  const toggleAgent = async (m) => {
    setBusy(m.user_id);
    const next = !m.is_agent;
    setMembers(prev => prev.map(x => x.user_id === m.user_id ? { ...x, is_agent: next } : x));
    try {
      if (m.is_agent) {
        await axios.delete(`${API}/service-desk/portals/${portalId}/agents/${m.user_id}`, { withCredentials: true, headers: getOrgHeaders() });
      } else {
        await axios.post(`${API}/service-desk/portals/${portalId}/agents`, { user_id: m.user_id }, { withCredentials: true, headers: getOrgHeaders() });
      }
    } catch (err) {
      // Roll the optimistic flip back so the switch reflects reality.
      setMembers(prev => prev.map(x => x.user_id === m.user_id ? { ...x, is_agent: m.is_agent } : x));
      toast.error(err?.response?.data?.detail || 'Failed');
    }
    finally { setBusy(null); }
  };

  const inviteByEmail = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/portals/${portalId}/agents`,
        { email: inviteEmail.trim() }, { withCredentials: true, headers: getOrgHeaders() });
      setInviteEmail('');
      if (data.invited) {
        toast.success(`Invitation created for ${data.email}${data.email_sent ? ' (emailed)' : ' — email not sent'}`);
      } else {
        toast.success('Agent added');
        load();
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add agent'); }
    finally { setInviting(false); }
  };

  const agentCount = members.filter(m => m.is_agent).length;

  return (
    <div className="space-y-4" data-testid="sd-agents-tab">
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
        Agents staff this portal queue. <strong>Owners &amp; admins always see every portal.</strong> Other members only see the portals they are added to here. Agents are internal users and count toward your paid seats.
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <Label className="text-xs">Invite a new teammate as an agent</Label>
        <div className="flex gap-2 mt-1">
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@company.com"
            data-testid="sd-agent-invite-email" />
          <Button onClick={inviteByEmail} disabled={inviting} data-testid="sd-agent-invite-btn">
            {inviting ? 'Adding…' : 'Add agent'}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-1.5">Existing members are added instantly; brand-new emails get a workspace invite and are assigned automatically when they accept (uses a paid seat).</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-zinc-900">Workspace members</h2>
          <span className="text-xs text-zinc-500">{agentCount} agent{agentCount === 1 ? '' : 's'} on this portal</span>
        </div>
        {loading ? <p className="text-xs text-zinc-400">Loading…</p> : (
          <div className="divide-y divide-zinc-100">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between py-2.5 gap-3" data-testid={`sd-agent-row-${m.user_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{m.name || m.email}</p>
                  <p className="text-xs text-zinc-500 truncate">{m.email} · {m.role}</p>
                  {m.all_portals ? (
                    <span className="inline-block mt-1 text-[10px] text-zinc-400">Access to all portals</span>
                  ) : (m.portals || []).length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1" data-testid={`sd-agent-portals-${m.user_id}`}>
                      {m.portals.map(p => (
                        <span key={p.portal_id} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200">{p.name}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="inline-block mt-1 text-[10px] text-zinc-400">No portals yet</span>
                  )}
                </div>
                {m.role === 'owner' || m.role === 'admin' ? (
                  <span className="text-[11px] text-zinc-400 flex-shrink-0">All portals</span>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[11px] text-zinc-500">{m.is_agent ? 'Agent' : 'No access'}</span>
                    <Switch checked={m.is_agent} disabled={busy === m.user_id}
                      onCheckedChange={() => toggleAgent(m)} data-testid={`sd-agent-toggle-${m.user_id}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-tab: Customers (per-portal end users + access policy) ───────────
function CustomersTab({ portal, savePortal, API, getOrgHeaders, portalId }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/service-desk/customers?portal_id=${portalId}`,
        { withCredentials: true, headers: getOrgHeaders() });
      setCustomers(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load customers'); }
    finally { setLoading(false); }
  }, [API, getOrgHeaders, portalId]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const { data } = await axios.post(`${API}/service-desk/customers`,
        { email: email.trim(), name: name.trim(), portal_id: portalId },
        { withCredentials: true, headers: getOrgHeaders() });
      setEmail(''); setName('');
      setLastInvite({ email: data.email, link: data.invite_link, sent: data.email_sent, error: data.email_error });
      toast.success(data.reinvited ? 'Existing customer granted access to this portal' : `Invited ${data.email}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to invite'); }
    finally { setInviting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this customer?')) return;
    try {
      await axios.delete(`${API}/service-desk/customers/${id}`, { withCredentials: true, headers: getOrgHeaders() });
      setCustomers(prev => prev.filter(c => c.customer_id !== id));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="sd-customers-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900">
        Customers are external end users — <strong>free &amp; unlimited</strong>, never counted as seats, and can only see this portal.
      </div>

      {/* Access policy */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4">
        <h2 className="text-sm font-semibold text-zinc-900">Who can raise requests here?</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-zinc-900">Allow anonymous submissions</p>
            <p className="text-xs text-zinc-500">Anyone with the link can submit without an account. Turn off for an invite-only portal.</p>
          </div>
          <Switch checked={portal.allow_anonymous !== false} onCheckedChange={(v) => savePortal({ allow_anonymous: v })}
            data-testid="sd-portal-allow-anonymous" />
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
          <div>
            <p className="text-sm font-medium text-zinc-900">Allow customer self-signup</p>
            <p className="text-xs text-zinc-500">Let end users create their own account to track requests (free, no seat).</p>
          </div>
          <Switch checked={!!portal.allow_signup} onCheckedChange={(v) => savePortal({ allow_signup: v })}
            data-testid="sd-portal-allow-signup" />
        </div>
      </div>

      {/* Invite */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <Label className="text-xs">Invite a customer to this portal</Label>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 mt-1">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@company.com" data-testid="sd-customer-invite-email" />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)" data-testid="sd-customer-invite-name" />
          <Button onClick={invite} disabled={inviting} data-testid="sd-customer-invite-btn">{inviting ? 'Inviting…' : 'Invite'}</Button>
        </div>
        {lastInvite && (
          <div className={`mt-3 rounded-md p-2.5 text-xs ${lastInvite.sent ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-amber-50 text-amber-900 border border-amber-200'}`} data-testid="sd-customer-invite-banner">
            {lastInvite.sent ? `Magic link emailed to ${lastInvite.email}.` : `Email delivery FAILED${lastInvite.error ? ` (${lastInvite.error})` : ''}. Copy this link and send it manually:`}
            {!lastInvite.sent && lastInvite.link && (
              <div className="mt-1.5 flex items-center gap-2">
                <code className="flex-1 break-all bg-white rounded px-2 py-1 ring-1 ring-amber-200">{lastInvite.link}</code>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(lastInvite.link); toast.success('Copied'); }}>
                  <Copy size={12} />
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-zinc-900 mb-3">Customers on this portal</h2>
        {loading ? <p className="text-xs text-zinc-400">Loading…</p> : customers.length === 0 ? (
          <p className="text-xs text-zinc-400 italic">No customers yet. Invite one above, or enable self-signup.</p>
        ) : (
          <div className="divide-y divide-zinc-100">
            {customers.map(c => (
              <div key={c.customer_id} className="flex items-center justify-between py-2.5" data-testid={`sd-customer-row-${c.customer_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-900 truncate">{c.name || c.email}</p>
                  <p className="text-xs text-zinc-500 truncate">
                    {c.email} · {(c.password_hash || c.password_set_at || c.self_signup) ? 'Active' : (c.external_ref?.source === 'jira' ? 'Imported (no email sent)' : 'Invited')}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => remove(c.customer_id)} className="text-red-600" data-testid={`sd-customer-remove-${c.customer_id}`}>
                  <Trash size={13} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Sub-tab: SLA & Routing ─────────────────────────────────────────────
const PRIORITIES = [
  { key: 'urgent', label: 'Urgent', tone: 'text-red-600' },
  { key: 'high', label: 'High', tone: 'text-orange-600' },
  { key: 'medium', label: 'Medium', tone: 'text-blue-600' },
  { key: 'low', label: 'Low', tone: 'text-zinc-500' },
];
const DEFAULT_TARGETS = {
  urgent: { response_mins: 60, resolution_mins: 240 },
  high: { response_mins: 240, resolution_mins: 1440 },
  medium: { response_mins: 480, resolution_mins: 2880 },
  low: { response_mins: 1440, resolution_mins: 7200 },
};
const minsToHrs = (m) => (m ? +(m / 60).toFixed(2) : 0);
const hrsToMins = (h) => { const n = Number(h); return Number.isFinite(n) && n > 0 ? Math.round(n * 60) : 0; };

function SlaRoutingTab({ portal, savePortal, requestTypes, API, getOrgHeaders, portalId }) {
  const [agents, setAgents] = useState([]);
  const [slaEnabled, setSlaEnabled] = useState(portal.sla_policy?.enabled === true);
  const [targets, setTargets] = useState(() => {
    const base = {};
    for (const p of PRIORITIES) base[p.key] = portal.sla_policy?.targets?.[p.key] || DEFAULT_TARGETS[p.key];
    return base;
  });
  const [roundRobin, setRoundRobin] = useState(portal.round_robin === true);
  const [rules, setRules] = useState(portal.assignment_rules || []);
  const [savingSla, setSavingSla] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [editCat, setEditCat] = useState('default');
  const [categoryTargets, setCategoryTargets] = useState(() => portal.sla_policy?.category_targets || {});

  const cloneTargets = (src) => Object.fromEntries(PRIORITIES.map(p => [p.key, { ...(src[p.key] || DEFAULT_TARGETS[p.key]) }]));
  const current = editCat === 'default' ? targets : (categoryTargets[editCat] || cloneTargets(targets));
  const hasOverride = editCat !== 'default' && !!categoryTargets[editCat];

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/service-desk/portals/${portalId}/agents`, { withCredentials: true, headers: getOrgHeaders() });
        setAgents((data.members || []).filter(m => m.is_agent || m.all_portals));
      } catch (_) { /* non-blocking */ }
    })();
  }, [API, getOrgHeaders, portalId]);

  const setTarget = (pk, field, hours) => {
    const key = field === 'response' ? 'response_mins' : 'resolution_mins';
    if (editCat === 'default') {
      setTargets(t => ({ ...t, [pk]: { ...t[pk], [key]: hrsToMins(hours) } }));
    } else {
      setCategoryTargets(ct => {
        const base = ct[editCat] || cloneTargets(targets);
        return { ...ct, [editCat]: { ...base, [pk]: { ...base[pk], [key]: hrsToMins(hours) } } };
      });
    }
  };

  const clearOverride = (cat) => {
    setCategoryTargets(ct => { const n = { ...ct }; delete n[cat]; return n; });
  };

  const saveSla = async () => {
    setSavingSla(true);
    await savePortal({ sla_policy: { enabled: slaEnabled, targets, category_targets: categoryTargets } });
    setSavingSla(false);
    toast.success('SLA policy saved');
  };

  const saveRouting = async () => {
    setSavingRouting(true);
    await savePortal({ round_robin: roundRobin, assignment_rules: rules.filter(r => r.assignee_user_id) });
    setSavingRouting(false);
    toast.success('Routing saved');
  };

  const addRule = () => setRules(r => [...r, { id: `ar_${Date.now()}`, request_type_id: null, priority: null, assignee_user_id: agents[0]?.user_id || null }]);
  const updateRule = (id, patch) => setRules(r => r.map(x => x.id === id ? { ...x, ...patch } : x));
  const removeRule = (id) => setRules(r => r.filter(x => x.id !== id));

  return (
    <div className="space-y-5" data-testid="sd-routing-tab">
      {/* SLA */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold text-zinc-900">Service-level agreements</h2>
          <Switch checked={slaEnabled} onCheckedChange={setSlaEnabled} data-testid="sd-sla-enabled" />
        </div>
        <p className="text-xs text-zinc-500 mb-4">Set response &amp; resolution targets per priority. Tickets get an SLA clock and a breach badge in the queue.</p>
        {slaEnabled && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-zinc-100">
              <span className="text-xs text-zinc-500">Editing SLA for:</span>
              <Select value={editCat} onValueChange={setEditCat}>
                <SelectTrigger className="h-8 w-[210px]" data-testid="sd-sla-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (all types)</SelectItem>
                  {CATEGORY_OPTIONS.map(c => (
                    <SelectItem key={c} value={c}>{categoryLabel(c)}{categoryTargets[c] ? ' · custom' : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasOverride && (
                <Button variant="ghost" size="sm" className="text-xs text-zinc-500 h-8" onClick={() => clearOverride(editCat)} data-testid="sd-sla-clear-override">
                  Reset to default
                </Button>
              )}
            </div>
            {editCat !== 'default' && !hasOverride && (
              <p className="text-[11px] text-amber-600 bg-amber-50 rounded px-2 py-1.5">
                {categoryLabel(editCat)} tickets currently use the default targets. Edit the values below to create a faster (or slower) SLA just for {categoryLabel(editCat)}.
              </p>
            )}
            <div className="grid grid-cols-[1fr_140px_140px] gap-2 text-[11px] uppercase tracking-wider text-zinc-400 font-semibold px-1">
              <span>Priority</span><span>First response (hrs)</span><span>Resolution (hrs)</span>
            </div>
            {PRIORITIES.map(p => (
              <div key={p.key} className="grid grid-cols-[1fr_140px_140px] gap-2 items-center">
                <span className={`text-sm font-medium ${p.tone}`}>{p.label}</span>
                <Input type="number" min="0" step="0.5" value={minsToHrs(current[p.key]?.response_mins)}
                  onChange={(e) => setTarget(p.key, 'response', e.target.value)} data-testid={`sd-sla-${p.key}-response`} />
                <Input type="number" min="0" step="0.5" value={minsToHrs(current[p.key]?.resolution_mins)}
                  onChange={(e) => setTarget(p.key, 'resolution', e.target.value)} data-testid={`sd-sla-${p.key}-resolution`} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={saveSla} disabled={savingSla} data-testid="sd-sla-save">{savingSla ? 'Saving…' : 'Save SLA'}</Button>
        </div>
      </div>

      {/* Routing */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-zinc-900 mb-1">Assignment rules</h2>
        <p className="text-xs text-zinc-500 mb-4">Auto-assign new tickets. Rules run top-to-bottom; the first match wins. If nothing matches, round-robin (if on) shares tickets across agents.</p>

        <div className="space-y-2 mb-4">
          {rules.length === 0 && <p className="text-xs text-zinc-400 italic">No rules yet — add one below.</p>}
          {rules.map(rule => (
            <div key={rule.id} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`sd-rule-${rule.id}`}>
              <Select value={rule.request_type_id || 'any'} onValueChange={(v) => updateRule(rule.id, { request_type_id: v === 'any' ? null : v })}>
                <SelectTrigger className="text-xs"><SelectValue placeholder="Request type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any request type</SelectItem>
                  {requestTypes.filter(rt => rt.portal_id === portalId).map(rt => <SelectItem key={rt.request_type_id} value={rt.request_type_id}>{rt.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={rule.priority || 'any'} onValueChange={(v) => updateRule(rule.id, { priority: v === 'any' ? null : v })}>
                <SelectTrigger className="text-xs"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any priority</SelectItem>
                  {PRIORITIES.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={rule.assignee_user_id || ''} onValueChange={(v) => updateRule(rule.id, { assignee_user_id: v })}>
                <SelectTrigger className="text-xs"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>
                  {agents.map(a => <SelectItem key={a.user_id} value={a.user_id}>{a.name || a.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" onClick={() => removeRule(rule.id)} className="text-red-600"><Trash size={13} /></Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addRule} disabled={agents.length === 0} data-testid="sd-rule-add">
          <Plus size={13} className="mr-1" /> Add rule
        </Button>
        {agents.length === 0 && <p className="text-[11px] text-amber-600 mt-2">Add agents to this portal first (Agents tab) to create assignment rules.</p>}

        <div className="flex items-center justify-between pt-4 mt-4 border-t border-zinc-100">
          <div>
            <p className="text-sm font-medium text-zinc-900">Round-robin fallback</p>
            <p className="text-xs text-zinc-500">Share unmatched tickets evenly across this portal&apos;s agents.</p>
          </div>
          <Switch checked={roundRobin} onCheckedChange={setRoundRobin} data-testid="sd-round-robin" />
        </div>

        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={saveRouting} disabled={savingRouting} data-testid="sd-routing-save">{savingRouting ? 'Saving…' : 'Save routing'}</Button>
        </div>
      </div>
    </div>
  );
}
